import type {
  TooltipContentProps as RadixTooltipContentProps,
  TooltipProps as RadixTooltipProps,
} from '@radix-ui/react-tooltip';
import React, { RefAttributes } from 'react';
import type { SvgIconProps } from 'ui/icons/SvgIcon';

export enum TipPlacement {
  Top = 'top',
  Bottom = 'bottom',
  Right = 'right',
  Left = 'left',
}

export enum TipAlign {
  Center = 'center',
  Start = 'start',
  End = 'end',
}

export enum FocusStyle {
  Default = 'default',
  Round = 'round',
}

export type TooltipContentProps = RadixTooltipContentProps &
  RefAttributes<HTMLDivElement> & {
    $animationDuration: TooltipProps['animationDuration'];
  };

export type TooltipProps = {
  /** The preferred alignment against the trigger. May change when collisions occur. */
  align?: TipAlign;
  /** The duration of the animation that occurs when a mouse enters a tooltip trigger until the tooltip opens. */
  animationDuration?: number;
  /**
   * Value to set for Popover.Content's avoidCollisions prop - if true, will avoid collision/cut off in the UI
   * when possible by automatically adjusting tooltip placement
   * */
  automaticallyAvoidCollisions?: boolean;
  /** styled-component param for applying the correct focus state css.
   *  only used when custom focus styling needs to be applied,
   *  i.e. if the trigger is an icon. */
  $focusStyle?: FocusStyle;
  /** styled-component param for applying the correct focus height css.
   *  only used when custom focus styling needs to be applied,
   *  i.e. if the trigger is an icon. */
  'data-test'?: string;
  children: React.ReactNode;
  /** className for the react component. Used by styled-components to apply styles to the trigger element. */
  className?: string;
  /** The duration from when the mouse enters a tooltip trigger until the tooltip opens. */
  delayDuration?: number;
  /** How much time a user has to enter another trigger without incurring a delay again. */
  skipDelayDuration?: number;
  /** Whether the tooltip is enabled, for conditional display of a tooltip. */
  isEnabled?: boolean;
  /** Whether the tooltip has a dashed, underlined styling applied. */
  isUnderlined?: boolean;
  /** Whether the tooltip has a grey dashed, underlined styling applied. */
  isUnderlinedGrey?: boolean;
  /** whether tooltip is used on a button component. if set to true, makes sure that focus state is only on button,
   * not on the tooltip, then the button */
  isUsedOnButton?: boolean;
  /** Determines the location of the tooltip. */
  placement?: TipPlacement;
  /** Content that shows up in the tooltip */
  tooltipContent: React.ReactNode;
  /** Custom tooltip content component. */
  ContentComponent?: React.ComponentType<TooltipContentProps>;
} & RadixTooltipProps;

export type HelpTooltipProps = {
  iconProps?: SvgIconProps;
} & Omit<TooltipProps, 'children'>;
